import { Orientation, PosterSettings } from '@/types/canvas';

/**
 * Rozměry mockup fotografie (living-room)
 * URL: https://mir-s3-cdn-cf.behance.net/project_modules/fs/2e061099272483.5eef57cb4080f.jpg
 */
const LIVING_ROOM_MOCKUP = {
  width: 1400, // Odhadovaná efektivní šířka pro výpočty
  height: 900, // Odhadovaná efektivní výška pro výpočty
  aspectRatio: 1400 / 900, // ~1.56 (landscape)
};

/**
 * Nastavení světla pro stínové efekty
 */
const LIGHT_SOURCE = {
  x: 0.2, // Světlo přichází zleva (20% z šířky)
  y: 0.1, // Světlo přichází shora (10% z výšky)
  intensity: 0.8, // Intenzita světla (0-1)
};

/**
 * Automaticky vypočítá optimální mockup transformace podle poměru stran posteru
 */
export function calculateMockupTransform(
  orientation: Orientation
): Partial<PosterSettings> {
  // const posterAspectRatio = canvasWidth / canvasHeight;

  // Portrait (svislý formát) - referenční je B2 (500x700)
  if (orientation === 'portrait') {
    // Použijeme fixní referenční výšku B2 (700mm) pro výpočet měřítka
    // Tím zajistíme, že menší formáty (A2, A3, A4) budou v mockupu vypadat menší
    const REFERENCE_HEIGHT = 700;
    const targetMockupHeightRatio = 0.55; // B2 zabere 55% výšky mockupu

    // Měřítko je konstantní vůči referenční velikosti (B2)
    // Výsledná vizuální velikost bude: canvasHeight * scale
    // Pro B2: 700 * (Target / 700) = Target
    // Pro A2: 594 * (Target / 700) = ~0.85 * Target (správně menší)
    const scale = (LIVING_ROOM_MOCKUP.height * targetMockupHeightRatio) / REFERENCE_HEIGHT;

    return {
      offsetX: 0,
      offsetY: -30,
      scale: Math.min(0.45, scale), // Cap na rozumné maximum
      perspective: 1400,
      rotateX: -4,
      rotateY: 4,
      rotateZ: 0,
    };
  }

  // Landscape (vodorovný formát) - referenční je B2 landscape (700x500)
  const REFERENCE_WIDTH = 700;
  const targetMockupWidthRatio = 0.45; // B2 landscape zabere 45% šířky mockupu
  const scale = (LIVING_ROOM_MOCKUP.width * targetMockupWidthRatio) / REFERENCE_WIDTH;

  return {
    offsetX: 0,
    offsetY: -20,
    scale: Math.min(0.5, scale),
    perspective: 1300,
    rotateX: -3,
    rotateY: 3,
    rotateZ: 0,
  };
}

/**
 * Vypočítá dynamické stíny podle pozice a rotace posteru
 */
export function calculateDynamicShadows(
  offsetX: number,
  offsetY: number,
  rotateX: number,
  rotateY: number,
  rotateZ: number,
  scale: number,
  canvasWidth: number,
  canvasHeight: number
): {
  wallShadow: React.CSSProperties;
  groundShadow: React.CSSProperties;
  innerShadow: React.CSSProperties;
} {
  // Základní offset stínu podle pozice světla
  const baseShadowX = (LIGHT_SOURCE.x - 0.5) * 50; // -15 až +15px
  const baseShadowY = (LIGHT_SOURCE.y - 0.5) * 40; // -20 až +20px

  // Rozšíření stínu podle rotací
  const shadowOffsetX = baseShadowX + (rotateY * 8); // Rotace Y ovlivní horizontální stín
  const shadowOffsetY = baseShadowY + (rotateX * 6); // Rotace X ovlivní vertikální stín

  // Rozmazání stínu podle vzdálenosti od světla
  const distanceFromLight = Math.sqrt(
    Math.pow(offsetX - (LIGHT_SOURCE.x * canvasWidth), 2) +
    Math.pow(offsetY - (LIGHT_SOURCE.y * canvasHeight), 2)
  );
  const maxDistance = Math.sqrt(Math.pow(canvasWidth, 2) + Math.pow(canvasHeight, 2));
  const blurIntensity = 8 + (distanceFromLight / maxDistance) * 12; // 8-20px blur

  // Průhlednost stínu podle vzdálenosti
  const shadowOpacity = Math.max(0.15, LIGHT_SOURCE.intensity - (distanceFromLight / maxDistance) * 0.3);

  // Stín na stěně (za posterem) - realističtější
  const wallShadow: React.CSSProperties = {
    position: 'absolute',
    top: `${-shadowOffsetY}px`,
    left: `${-shadowOffsetX}px`,
    width: `${canvasWidth * scale}px`,
    height: `${canvasHeight * scale}px`,
    background: 'radial-gradient(ellipse at 25% 15%, rgba(0,0,0,0.5) 0%, rgba(0,0,0,0.2) 40%, transparent 80%)',
    filter: `blur(${blurIntensity}px)`,
    opacity: shadowOpacity,
    zIndex: -1,
    pointerEvents: 'none',
  };

  // Stín na zemi (pod posterem) - realističtější
  const groundShadow: React.CSSProperties = {
    position: 'absolute',
    top: `${canvasHeight * scale - 15}px`,
    left: `${-shadowOffsetX * 0.8}px`,
    width: `${canvasWidth * scale}px`,
    height: '25px',
    background: 'linear-gradient(135deg, rgba(0,0,0,0.4) 0%, rgba(0,0,0,0.2) 50%, transparent 100%)',
    filter: `blur(${blurIntensity * 0.9}px)`,
    opacity: shadowOpacity * 0.9,
    zIndex: -1,
    pointerEvents: 'none',
    transform: `rotateZ(${rotateZ}deg)`,
  };

  // Vnitřní stín rámečku
  const innerShadow: React.CSSProperties = {
    position: 'absolute',
    inset: '0',
    boxShadow: `inset ${shadowOffsetX * 0.3}px ${shadowOffsetY * 0.3}px 8px rgba(0,0,0,0.1)`,
    zIndex: 1,
    pointerEvents: 'none',
  };

  return {
    wallShadow,
    groundShadow,
    innerShadow,
  };
}

/**
 * Vrátí optimální měřítko podle velikosti posteru
 */
export function calculateOptimalScale(
  canvasWidth: number,
  canvasHeight: number,
  mockupWidth: number = 1920, // Předpokládaná šířka mockup fotky
  mockupHeight: number = 1080 // Předpokládaná výška mockup fotky
): number {
  // Maximální poměr, který poster může zabrat v mockupu
  const maxWidthRatio = 0.35;
  const maxHeightRatio = 0.45;

  const scaleByWidth = (mockupWidth * maxWidthRatio) / canvasWidth;
  const scaleByHeight = (mockupHeight * maxHeightRatio) / canvasHeight;

  // Použít menší měřítko, aby se poster vždy vešel
  return Math.min(scaleByWidth, scaleByHeight);
}

